import { api, setAuthentication } from './api';

/**
 * Servicio para manejar las gráficas Sankey
 * Basado en las especificaciones del documento API_SANKEY_CHANGES.md
 * Versión API: 2.0
 */

/**
 * Normaliza las unidades para búsqueda flexible (case-insensitive y parcial)
 * @param {string[]} unidades - Array de unidades a normalizar
 * @returns {string[]} Array de unidades normalizadas
 */
const normalizeUnidades = (unidades) => {
  if (!Array.isArray(unidades) || unidades.length === 0) {
    return [];
  }
  
  // Convertir a minúsculas y eliminar espacios para búsqueda flexible
  return unidades.map(unidad => 
    unidad.toString().toLowerCase().trim()
  ).filter(unidad => unidad.length > 0);
};

/**
 * Helper para convertir fechas a formato ISO string si es necesario
 * @param {string|Date} fecha - Fecha en formato string o Date
 * @returns {string} Fecha en formato ISO (YYYY-MM-DD)
 */
const formatFecha = (fecha) => {
  if (!fecha) return '';
  
  if (fecha instanceof Date) {
    return fecha.toISOString().split('T')[0];
  }
  
  // Si ya es string, asumimos que está en formato correcto
  return fecha;
};

/**
 * Obtiene datos de gráfica Sankey por proyecto
 * @param {string} proyectoId - ID del proyecto
 * @param {Object} params - Parámetros opcionales
 * @param {string|Date} params.fechaInicio - Fecha de inicio (ISO 8601) - Opcional
 * @param {string|Date} params.fechaFin - Fecha de fin (ISO 8601) - Opcional
 * @param {string[]} params.unidades - Filtrar por unidades específicas - Opcional
 * @returns {Promise} Respuesta con datos de Sankey
 */
export const getSankeyByProyecto = async (proyectoId, params = {}) => {
  try {
    setAuthentication();
    const queryParams = new URLSearchParams();
    
    if (params.fechaInicio) {
      queryParams.append('fechaInicio', formatFecha(params.fechaInicio));
    }
    if (params.fechaFin) {
      queryParams.append('fechaFin', formatFecha(params.fechaFin));
    }
    if (params.unidades && params.unidades.length > 0) {
      const normalizedUnidades = normalizeUnidades(params.unidades);
      if (normalizedUnidades.length > 0) {
        queryParams.append('unidades', normalizedUnidades.join(','));
      }
    }

    const url = `/sankey/proyecto/${proyectoId}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    console.error('Error al obtener gráfica Sankey por proyecto:', error);
    throw error;
  }
};

/**
 * Obtiene datos de gráfica Sankey por elemento
 * @param {string} elementoId - ID del elemento
 * @param {Object} params - Parámetros opcionales
 * @param {string|Date} params.fechaInicio - Fecha de inicio (ISO 8601) - Opcional
 * @param {string|Date} params.fechaFin - Fecha de fin (ISO 8601) - Opcional
 * @param {string[]} params.unidades - Filtrar por unidades específicas - Opcional
 * @returns {Promise} Respuesta con datos de Sankey
 */
export const getSankeyByElemento = async (elementoId, params = {}) => {
  try {
    setAuthentication();
    const queryParams = new URLSearchParams();
    
    if (params.fechaInicio) {
      queryParams.append('fechaInicio', formatFecha(params.fechaInicio));
    }
    if (params.fechaFin) {
      queryParams.append('fechaFin', formatFecha(params.fechaFin));
    }
    if (params.unidades && params.unidades.length > 0) {
      const normalizedUnidades = normalizeUnidades(params.unidades);
      if (normalizedUnidades.length > 0) {
        queryParams.append('unidades', normalizedUnidades.join(','));
      }
    }

    const url = `/sankey/elemento/${elementoId}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    console.error('Error al obtener gráfica Sankey por elemento:', error);
    throw error;
  }
};

/**
 * Obtiene datos de gráfica Sankey por tarjeta
 * @param {string} tarjetaId - ID de la tarjeta
 * @param {Object} params - Parámetros opcionales
 * @param {string|Date} params.fechaInicio - Fecha de inicio (ISO 8601) - Opcional
 * @param {string|Date} params.fechaFin - Fecha de fin (ISO 8601) - Opcional
 * @param {string[]} params.unidades - Filtrar por unidades específicas - Opcional
 * @returns {Promise} Respuesta con datos de Sankey
 */
export const getSankeyByTarjeta = async (tarjetaId, params = {}) => {
  try {
    setAuthentication();
    const queryParams = new URLSearchParams();
    
    if (params.fechaInicio) {
      queryParams.append('fechaInicio', formatFecha(params.fechaInicio));
    }
    if (params.fechaFin) {
      queryParams.append('fechaFin', formatFecha(params.fechaFin));
    }
    if (params.unidades && params.unidades.length > 0) {
      const normalizedUnidades = normalizeUnidades(params.unidades);
      if (normalizedUnidades.length > 0) {
        queryParams.append('unidades', normalizedUnidades.join(','));
      }
    }

    const url = `/sankey/tarjeta/${tarjetaId}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    console.error('Error al obtener gráfica Sankey por tarjeta:', error);
    throw error;
  }
};

/**
 * Transforma los datos de la nueva API al formato esperado por el componente Sankey existente
 * @param {Object} sankeyData - Datos de la nueva API
 * @returns {Object} Datos transformados para el componente
 */
export const transformSankeyData = (sankeyData) => {
  if (!sankeyData || !sankeyData.nodes || !sankeyData.links) {
    return null;
  }

  // Crear el dataset para Google Charts
  const chartData = [['From', 'To', 'Weight']];

  sankeyData.links.forEach(link => {
    const fromNode = sankeyData.nodes.find(n => n.id === link.source);
    const toNode = sankeyData.nodes.find(n => n.id === link.target);
    
    if (fromNode && toNode) {
      chartData.push([fromNode.name, toNode.name, link.value]);
    }
  });

  return {
    chartData,
    metadata: sankeyData.metadata,
    nodes: sankeyData.nodes,
    links: sankeyData.links
  };
};

/**
 * Helper para obtener fechas por defecto (últimos 7 días)
 * @returns {Object} Objeto con fechaInicio y fechaFin por defecto
 */
export const getDefaultDateRange = () => {
  const fechaFin = new Date();
  const fechaInicio = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
  
  return {
    fechaInicio: formatFecha(fechaInicio),
    fechaFin: formatFecha(fechaFin)
  };
};